import Phaser from 'phaser';
import { GAME_W, GAME_H, setupCamera } from '../dpr.js';
import { GameStateManager } from '../state/GameStateManager';
import { TOME_DEFINITIONS, TOME_IDS, type TomeId, getPassiveBonuses } from '@roe/shared';
import { drawBanIcon } from '../utils/glyphs';

/**
 * Pre-battle Book Ban modal.
 *
 * Launched from SigilLoadoutScene's confirm step and runs before BattleScene starts.
 * The player picks up to 2 tomes to ban from the run — banned tomes never roll as choice
 * options in-run, and each ban imposes a −1 % attack / −1 % max HP party-wide malus.
 *
 * Interaction:
 *   - Click a book cover to ban / unban (red strike + red frame when banned).
 *   - Hover a book to see the full description in a side tooltip — fixes the
 *     on-cover text truncation problem ("Scales wave size — at max rank 99 :…").
 *   - Mousewheel to scroll the grid when it overflows the viewport.
 */
const MAX_BANS = 3;
/** Runes charged per ban on confirm. Replaces the previous −1 % atk / −1 % maxHP
 *  per-ban party-wide malus (removed 2026-04-24 — bans are now a paid choice,
 *  not a stat trade-off). */
const BAN_COST_RUNES = 5;

export class BookBanScene extends Phaser.Scene {
  private gsm!: GameStateManager;
  private bannedIds: Set<TomeId> = new Set();
  private tileRefs: Array<{
    id: TomeId;
    bg: Phaser.GameObjects.Rectangle;
    border: Phaser.GameObjects.Rectangle;
    strikeGfx: Phaser.GameObjects.Graphics;
    spineColor: number;
    giltColor: number;
  }> = [];
  private counterText!: Phaser.GameObjects.Text;
  private confirmBg!: Phaser.GameObjects.Rectangle;
  private confirmTxt!: Phaser.GameObjects.Text;
  private gridContainer!: Phaser.GameObjects.Container;
  private scrollY = 0;
  private maxScroll = 0;
  private viewportTop = 0;
  private viewportBottom = 0;
  private hoverTooltip: Phaser.GameObjects.Container | null = null;

  constructor() {
    super({ key: 'BookBanScene' });
  }

  init(data: { gsm?: GameStateManager }): void {
    this.gsm = data.gsm || GameStateManager.getInstance();
  }

  create(): void {
    setupCamera(this, this.cameras.main, 'full');
    this.cameras.main.setBackgroundColor('#0a0604');
    if (!this.gsm) this.gsm = GameStateManager.getInstance();

    this.bannedIds = new Set();
    this.tileRefs = [];
    this.scrollY = 0;
    this.hoverTooltip = null;

    // ── Background (enchanted library look) ──────────────────────────────────
    // Cover-fit the menu_bg_enchanted image, then a dark overlay so the cards
    // read cleanly on top. Subtle gold vignette frame at the edges so the
    // scene feels like a tomb/library page rather than a bare grid.
    if (this.textures.exists('menu_bg_enchanted')) {
      const bgImg = this.add.image(GAME_W / 2, GAME_H / 2, 'menu_bg_enchanted')
        .setDepth(-2);
      const tex = this.textures.get('menu_bg_enchanted').getSourceImage();
      const scale = Math.max(GAME_W / tex.width, GAME_H / tex.height);
      bgImg.setScale(scale);
      bgImg.setAlpha(0.35);
    }
    // Dark overlay tint
    this.add.rectangle(GAME_W / 2, GAME_H / 2, GAME_W, GAME_H, 0x0a0604, 0.65).setDepth(-1);
    // Top + bottom ornamental bands (gradient strips that read as old book trim)
    this.add.rectangle(GAME_W / 2, 50, GAME_W, 100, 0x140a04, 0.85).setDepth(-1);
    this.add.rectangle(GAME_W / 2, GAME_H - 50, GAME_W, 100, 0x140a04, 0.85).setDepth(-1);
    // Gold divider lines
    this.add.rectangle(GAME_W / 2, 100, GAME_W - 40, 1, 0xc8973a, 0.55).setDepth(-1);
    this.add.rectangle(GAME_W / 2, GAME_H - 100, GAME_W - 40, 1, 0xc8973a, 0.55).setDepth(-1);
    // Corner gold L-brackets — top-left, top-right, bottom-left, bottom-right
    {
      const cornerLen = 28;
      const cgfx = this.add.graphics().setDepth(-1);
      cgfx.lineStyle(2, 0xc8973a, 0.85);
      const bL = 18, bT = 18, bR = GAME_W - 18, bB = GAME_H - 18;
      cgfx.beginPath();
      cgfx.moveTo(bL, bT + cornerLen); cgfx.lineTo(bL, bT); cgfx.lineTo(bL + cornerLen, bT);
      cgfx.moveTo(bR - cornerLen, bT); cgfx.lineTo(bR, bT); cgfx.lineTo(bR, bT + cornerLen);
      cgfx.moveTo(bL, bB - cornerLen); cgfx.lineTo(bL, bB); cgfx.lineTo(bL + cornerLen, bB);
      cgfx.moveTo(bR - cornerLen, bB); cgfx.lineTo(bR, bB); cgfx.lineTo(bR, bB - cornerLen);
      cgfx.strokePath();
    }

    // ── Header ────────────────────────────────────────────────────────────────
    const headerIconGfx = this.add.graphics();
    headerIconGfx.translateCanvas(GAME_W / 2 - 90, 22);
    headerIconGfx.scaleCanvas(1.3, 1.3);
    drawBanIcon(headerIconGfx, 0, 0, 0xc8973a, 0xe03030);

    const headerIconGfx2 = this.add.graphics();
    headerIconGfx2.translateCanvas(GAME_W / 2 + 90, 22);
    headerIconGfx2.scaleCanvas(1.3, 1.3);
    drawBanIcon(headerIconGfx2, 0, 0, 0xc8973a, 0xe03030);

    this.add.text(GAME_W / 2, 22, 'BAN BOOKS', {
      fontSize: '22px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: '#e8a030', fontStyle: 'bold', stroke: '#1a0a02', strokeThickness: 3,
    }).setOrigin(0.5).setResolution(2);

    {
      const freeBans = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).extraBookBan;
      const headerCopy = freeBans > 0
        ? `Ban up to ${MAX_BANS} books — first ${freeBans} ban${freeBans > 1 ? 's are' : ' is'} FREE (Banished Tome), additional bans cost ${BAN_COST_RUNES} ◆ each. Hover a book for full details.`
        : `Ban up to ${MAX_BANS} books — each ban removes that book from future choice rolls and costs ${BAN_COST_RUNES} ◆ on confirm. Hover a book for full details.`;
      this.add.text(GAME_W / 2, 48, headerCopy, {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840',
        align: 'center', wordWrap: { width: GAME_W - 80 }, lineSpacing: 2,
      }).setOrigin(0.5);
    }

    this.counterText = this.add.text(GAME_W / 2, 78, '', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#e0a060', fontStyle: 'bold',
    }).setOrigin(0.5);

    // ── Scrollable grid container ────────────────────────────────────────────
    // Viewport spans from the header (~y=96) to just above the footer (~y=548).
    // Grid rendering is wrapped in a Container that shifts vertically on wheel,
    // with a geometry mask clipping anything outside the viewport band.
    const COLS = 5;
    const tileW = 100;
    const tileH = 115;
    const gapX = 14;
    const gapY = 10;
    const gridW = COLS * tileW + (COLS - 1) * gapX;
    const startX = (GAME_W - gridW) / 2 + tileW / 2;
    const rowOffset = tileH / 2 + 4; // first tile's center relative to container top

    this.viewportTop = 96;
    this.viewportBottom = GAME_H - 50;
    const viewportH = this.viewportBottom - this.viewportTop;

    this.gridContainer = this.add.container(0, this.viewportTop).setDepth(0);

    TOME_IDS.forEach((id, i) => {
      const def = TOME_DEFINITIONS[id];
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const cx = startX + col * (tileW + gapX);
      const cy = rowOffset + row * (tileH + gapY);
      const unlocked = this.gsm.isTomeUnlocked(id);

      // Card colour treatment matched to the in-game book-choice popup per
      // user feedback: dark almost-black fill at moderate alpha so the
      // grid reads as ornate book covers, not warm-bronze tiles.
      const coverColor = unlocked ? 0x0d0a08 : 0x080604;
      const spineColor = unlocked ? 0x1a1108 : 0x0a0604;
      const giltColor  = unlocked ? 0x5a3600 : 0x2a1a0a;

      const bg = this.add.rectangle(cx, cy, tileW, tileH, coverColor, 0.85);
      const border = this.add.rectangle(cx, cy, tileW, tileH, 0, 0)
        .setStrokeStyle(1, giltColor, 0.9);
      const SPINE_W = 10;
      const spine = this.add.rectangle(cx - tileW / 2 + SPINE_W / 2, cy, SPINE_W, tileH, spineColor, 0.85);
      const spineAccent = this.add.rectangle(cx - tileW / 2 + SPINE_W + 1, cy, 1, tileH, giltColor, 0.7);
      const giltTop    = this.add.rectangle(cx + SPINE_W / 2, cy - tileH / 2 + 5, tileW - SPINE_W - 10, 1, giltColor, 0.55);
      const giltBottom = this.add.rectangle(cx + SPINE_W / 2, cy + tileH / 2 - 5, tileW - SPINE_W - 10, 1, giltColor, 0.55);

      const iconColor = unlocked ? def.color : '#404040';
      const iconText = this.add.text(cx + SPINE_W / 2, cy - 22, def.icon, {
        fontSize: '28px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: iconColor, fontStyle: 'bold',
      }).setOrigin(0.5);

      // Truncated descriptions removed per user feedback — hover tooltip
      // surfaces the full description, the cover only carries icon + name.
      const nameText = this.add.text(cx + SPINE_W / 2, cy + 14, def.name, {
        fontSize: '10px', fontFamily: 'Georgia, serif',
        color: unlocked ? def.color : '#505050', fontStyle: 'bold',
        wordWrap: { width: tileW - SPINE_W - 10 }, align: 'center', lineSpacing: 1,
      }).setOrigin(0.5, 0);

      const strikeGfx = this.add.graphics();
      strikeGfx.lineStyle(3, 0xd03030, 0.92);
      strikeGfx.beginPath();
      strikeGfx.moveTo(cx - tileW / 2 + 6, cy - tileH / 2 + 6);
      strikeGfx.lineTo(cx + tileW / 2 - 6, cy + tileH / 2 - 6);
      strikeGfx.moveTo(cx + tileW / 2 - 6, cy - tileH / 2 + 6);
      strikeGfx.lineTo(cx - tileW / 2 + 6, cy + tileH / 2 - 6);
      strikeGfx.strokePath();
      strikeGfx.setVisible(false);

      this.gridContainer.add([bg, border, spine, spineAccent, giltTop, giltBottom,
        iconText, nameText, strikeGfx]);

      if (unlocked) {
        bg.setInteractive({ useHandCursor: true });
        bg.on('pointerdown', () => this.toggleBan(id));
        bg.on('pointerover', () => {
          if (!this.bannedIds.has(id)) border.setStrokeStyle(1, 0xc8973a, 1);
          // Screen-space position for the tooltip: convert container-local cx/cy +
          // the container's current y offset into absolute.
          const absY = this.viewportTop + cy + this.scrollY;
          this.showHoverTooltip(def.name, def.description, cx, absY, tileW, tileH);
        });
        bg.on('pointerout', () => {
          if (!this.bannedIds.has(id)) border.setStrokeStyle(1, giltColor, 0.9);
          this.hideHoverTooltip();
        });
      }

      this.tileRefs.push({ id, bg, border, strikeGfx, spineColor, giltColor });
    });

    // Geometry mask so tiles outside the viewport band get clipped on scroll.
    const maskShape = this.make.graphics({ x: 0, y: 0 }, false);
    maskShape.fillStyle(0xffffff, 1);
    maskShape.fillRect(0, this.viewportTop, GAME_W, viewportH);
    this.gridContainer.setMask(maskShape.createGeometryMask());

    // Compute scroll range — how far the grid can shift up before its bottom edge
    // meets the viewport bottom. If the grid fits entirely, maxScroll = 0.
    const rows = Math.ceil(TOME_IDS.length / COLS);
    const contentH = rows * tileH + (rows - 1) * gapY + 8;
    this.maxScroll = Math.max(0, contentH - viewportH);

    // Wheel scroll — invert dy so scrolling down moves the content up.
    this.input.on('wheel', (_ptr: unknown, _gos: unknown, _dx: unknown, dy: number) => {
      if (this.maxScroll <= 0) return;
      this.scrollY = Math.max(-this.maxScroll, Math.min(0, this.scrollY - dy * 0.5));
      this.gridContainer.y = this.viewportTop + this.scrollY;
      this.hideHoverTooltip();
    });

    // ── Footer: Back | Skip | Confirm ────────────────────────────────────────
    const footerY = GAME_H - 22;
    const backBg = this.add.rectangle(GAME_W / 2 - 220, footerY, 110, 30, 0x1a0f08, 1)
      .setStrokeStyle(1, 0x5a3600).setInteractive({ useHandCursor: true });
    const backTxt = this.add.text(GAME_W / 2 - 220, footerY, '◀ BACK', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#8a6420', fontStyle: 'bold',
    }).setOrigin(0.5);
    // BACK returns all the way to the main menu, not the SigilLoadoutScene
    // mid-step (the loadout transitions a run-state — re-entering it from
    // back-button gave a stuck-feeling, user-reported "back doesn't bring me
    // back to main menu"). Pass `gsm` so MenuScene re-init keeps player state.
    const doBack = () => this.scene.start('MenuScene', { gsm: this.gsm });
    backBg.on('pointerover', () => { backTxt.setColor('#c8973a'); backBg.setStrokeStyle(1, 0x8a6420); });
    backBg.on('pointerout',  () => { backTxt.setColor('#8a6420'); backBg.setStrokeStyle(1, 0x5a3600); });
    backBg.on('pointerdown', doBack);
    backTxt.setInteractive({ useHandCursor: true }).on('pointerdown', doBack);

    const skipBg = this.add.rectangle(GAME_W / 2 - 70, footerY, 140, 30, 0x1a0f08, 1)
      .setStrokeStyle(1, 0x5a3600).setInteractive({ useHandCursor: true });
    const skipTxt = this.add.text(GAME_W / 2 - 70, footerY, 'SKIP (0 bans)', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#8a6420', fontStyle: 'bold',
    }).setOrigin(0.5);
    const doSkip = () => this.commit(true);
    skipBg.on('pointerover', () => { skipTxt.setColor('#c8973a'); skipBg.setStrokeStyle(1, 0x8a6420); });
    skipBg.on('pointerout',  () => { skipTxt.setColor('#8a6420'); skipBg.setStrokeStyle(1, 0x5a3600); });
    skipBg.on('pointerdown', doSkip);
    skipTxt.setInteractive({ useHandCursor: true }).on('pointerdown', doSkip);

    this.confirmBg = this.add.rectangle(GAME_W / 2 + 120, footerY, 180, 30, 0x1a0608, 1)
      .setStrokeStyle(2, 0x8b2000).setInteractive({ useHandCursor: true });
    this.confirmTxt = this.add.text(GAME_W / 2 + 120, footerY, 'CONFIRM & BEGIN', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#e8a030', fontStyle: 'bold',
    }).setOrigin(0.5);
    const doConfirm = () => {
      // Affordability gate — no-op click if the player can't pay for the
      // current selection (the confirm button is dimmed + relabelled already).
      const runeCost = this.bannedIds.size * BAN_COST_RUNES;
      if ((this.gsm.player.resources.loot ?? 0) < runeCost) return;
      this.commit(false);
    };
    this.confirmBg.on('pointerover', () => { this.confirmTxt.setColor('#ffe8a0'); this.confirmBg.setStrokeStyle(2, 0xc84000); });
    this.confirmBg.on('pointerout',  () => { this.confirmTxt.setColor('#e8a030'); this.confirmBg.setStrokeStyle(2, 0x8b2000); });
    this.confirmBg.on('pointerdown', doConfirm);
    this.confirmTxt.setInteractive({ useHandCursor: true }).on('pointerdown', doConfirm);

    // Scroll indicator chevrons (subtle, only render if grid overflows)
    if (this.maxScroll > 0) {
      this.add.text(GAME_W - 18, this.viewportTop + 10, '▲', {
        fontSize: '12px', fontFamily: 'Georgia, serif', color: '#5a3600',
      }).setOrigin(0.5).setDepth(5);
      this.add.text(GAME_W - 18, this.viewportBottom - 10, '▼', {
        fontSize: '12px', fontFamily: 'Georgia, serif', color: '#5a3600',
      }).setOrigin(0.5).setDepth(5);
    }

    this.input.keyboard?.once('keydown-ESC', doSkip);
    this.refreshCounter();
  }

  /** Render the hover tooltip showing the full tome description. Anchored beside the
   *  tile so it doesn't occlude the cover. */
  private showHoverTooltip(name: string, description: string, tileCx: number, tileAbsY: number, tileW: number, tileH: number): void {
    this.hideHoverTooltip();
    const lines = [name, '', description];
    const tip = this.add.container(0, 0).setDepth(200);
    const txt = this.add.text(0, 0, lines.join('\n'), {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#c8973a',
      backgroundColor: '#0a0604', padding: { x: 8, y: 6 }, lineSpacing: 3,
      wordWrap: { width: 220 }, align: 'left',
    });
    const border = this.add.rectangle(0, 0, txt.width, txt.height, 0, 0)
      .setStrokeStyle(1, 0x5a3600, 0.95).setOrigin(0, 0);
    tip.add([txt, border]);
    // Prefer rendering to the right of the tile; flip to the left if it would overflow.
    const margin = 6;
    let tx = tileCx + tileW / 2 + margin;
    if (tx + txt.width > GAME_W - margin) tx = tileCx - tileW / 2 - margin - txt.width;
    let ty = tileAbsY - txt.height / 2;
    if (ty < this.viewportTop) ty = this.viewportTop;
    if (ty + txt.height > this.viewportBottom) ty = this.viewportBottom - txt.height;
    tip.setPosition(tx, ty);
    this.hoverTooltip = tip;
  }

  private hideHoverTooltip(): void {
    this.hoverTooltip?.destroy();
    this.hoverTooltip = null;
  }

  private toggleBan(id: TomeId): void {
    if (this.bannedIds.has(id)) {
      this.bannedIds.delete(id);
    } else {
      if (this.bannedIds.size >= MAX_BANS) return;
      this.bannedIds.add(id);
    }
    const ref = this.tileRefs.find((t) => t.id === id);
    if (ref) {
      const banned = this.bannedIds.has(id);
      ref.strikeGfx.setVisible(banned);
      ref.border.setStrokeStyle(1, banned ? 0xd03030 : ref.giltColor, banned ? 1 : 0.9);
      ref.bg.setFillStyle(banned ? 0x2a0608 : 0x2a1a0a, 0.95);
    }
    this.refreshCounter();
  }

  private refreshCounter(): void {
    const n = this.bannedIds.size;
    // Banished Tome talent (CUNNING) — first `extraBookBan` bans are free.
    // Cost-charged bans are the OVERFLOW above the free allotment.
    const freeBans = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).extraBookBan;
    const paidBans = Math.max(0, n - freeBans);
    const runeCost = paidBans * BAN_COST_RUNES;
    const currentRunes = this.gsm.player.resources.loot ?? 0;
    const canAfford = currentRunes >= runeCost;
    let statusText: string;
    if (n === 0) {
      statusText = freeBans > 0
        ? `No bans selected — first ${freeBans} ban${freeBans > 1 ? 's' : ''} free, additional cost ${BAN_COST_RUNES} ◆ each.`
        : 'No bans selected — click a book to ban it.';
    } else {
      const freeUsed = Math.min(n, freeBans);
      statusText = freeBans > 0
        ? `${n}/${MAX_BANS} banned — ${freeUsed} free, ${paidBans} × ${BAN_COST_RUNES}◆ = ${runeCost} ◆  (you have ${currentRunes} ◆)`
        : `${n}/${MAX_BANS} banned — cost ${runeCost} ◆  (you have ${currentRunes} ◆)`;
    }
    this.counterText.setText(statusText);
    this.counterText.setColor(!canAfford ? '#ff8080' : n === MAX_BANS ? '#c8973a' : '#e0a060');
    // Soft-gate the confirm button — still visible but dimmed if the player
    // can't afford the rune cost on the current selection.
    if (this.confirmBg) {
      this.confirmBg.setAlpha(canAfford ? 1 : 0.45);
      this.confirmBg.setStrokeStyle(2, canAfford ? 0x8b2000 : 0x5a2000);
    }
    if (this.confirmTxt) {
      this.confirmTxt.setAlpha(canAfford ? 1 : 0.45);
      this.confirmTxt.setText(n === 0 ? 'CONFIRM & BEGIN' : canAfford ? (runeCost > 0 ? `CONFIRM (${runeCost} ◆)` : 'CONFIRM (FREE)') : `NOT ENOUGH ◆`);
    }
  }

  private commit(skip: boolean): void {
    const bans = skip ? [] : Array.from(this.bannedIds);
    this.gsm.player.bannedTomes = bans;
    // Mark pre-run setup as done so MainMenu's "Enter the Battlefield" knows
    // it can drop straight into combat. BACK from this scene leaves the flag
    // at false (or whatever it was), and the next play attempt re-routes
    // through SigilLoadout → BookBan.
    this.gsm.player.preRunSetupDone = true;
    // Spend runes for each PAID ban (free allotment from the Banished Tome
    // talent comes off the top). Spending clamps at 0 — confirm is gated on
    // affordability above, this is belt-and-suspenders.
    const freeBans = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).extraBookBan;
    const paidBans = Math.max(0, bans.length - freeBans);
    const runeCost = paidBans * BAN_COST_RUNES;
    if (runeCost > 0) {
      this.gsm.player.resources.loot = Math.max(0, (this.gsm.player.resources.loot ?? 0) - runeCost);
    }
    this.gsm.save(true);
    this.scene.start('BattleScene');
  }
}
